import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { listMynewsdeskItems } from "@/lib/mynewsdesk";

const FEATURED_CATEGORIES = [
  { slug: "news", label: "News", path: "/news", mnd: "news" },
  { slug: "blog-post", label: "Blog Post", path: "/insight/blog-post", mnd: "blog_post" },
  { slug: "pressrelease", label: "Press Releases", path: "/insight/press-release", mnd: "pressrelease" },
];

const norm = (v: string) => (v || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const Blog = () => {
  const { t, i18n } = useTranslation('blog');

  const { data: sections, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["insight-overview-v3"],
    staleTime: 10 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const slugs = FEATURED_CATEGORIES.map((c) => c.slug);
      const [dbResult, ...mndResults] = await Promise.all([
        supabase
          .from("blog_categories")
          .select("slug, blog_post_categories(blog_posts(id, title, slug, excerpt, content, featured_image, published_at, status))")
          .in("slug", slugs),
        ...FEATURED_CATEGORIES.map((c) => listMynewsdeskItems(c.mnd, 20).catch(() => [])),
      ]);
      if (dbResult.error) throw dbResult.error;
      const data = dbResult.data;

      return FEATURED_CATEGORIES.map((cat, idx) => {
        const mndPosts = (mndResults[idx] || []).map((r: any) => ({
          id: `mnd-${r.id}`,
          title: r.title,
          excerpt: r.summary,
          content: "",
          featured_image: r.image || r.imageThumb,
          published_at: r.publishedAt,
          __href: `/press/${r.slug}`,
        }));
        const mndTitles = new Set(mndPosts.map((p: any) => norm(p.title)));
        const foreignTitles = new Set(
          mndResults.flatMap((list: any, i: number) => (i === idx ? [] : (list || []).map((r: any) => norm(r.title)))),
        );
        const row: any = (data || []).find((r: any) => r.slug === cat.slug);
        const dbPosts = cat.mnd === "pressrelease"
          ? []
          : ((row?.blog_post_categories || []) as any[])
              .map((bpc: any) => bpc.blog_posts)
              .filter((p: any) => p && p.status === "published" && (!p.published_at || new Date(p.published_at) <= new Date()))
              .filter((p: any) => !mndTitles.has(norm(p.title)) && !foreignTitles.has(norm(p.title)));
        const posts = [...mndPosts, ...dbPosts]
          .sort((a: any, b: any) => new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime())
          .slice(0, 3);
        return { ...cat, posts };
      });
    },
  });

  const getExcerpt = (post: any) => {
    if (post.excerpt) return post.excerpt;
    if (post.content) { const stripped = post.content.replace(/<[^>]*>/g, ""); return stripped.substring(0, 160) + (stripped.length > 160 ? "..." : ""); }
    return "";
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString(i18n.language === 'sv' ? 'sv-SE' : 'en-US', { year: "numeric", month: "long", day: "numeric" });

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('insight.meta.title')}</title>
        <meta name="description" content={t('insight.meta.description')} />
        <link rel="canonical" href="https://cetasol.com/news" />
        <meta property="og:title" content={t('insight.meta.title')} />
        <meta property="og:description" content={t('insight.meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/news" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('insight.meta.title')} />
        <meta name="twitter:description" content={t('insight.meta.description')} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Insight", "item": "https://cetasol.com/news" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="min-h-screen pt-32 pb-16 section-fade">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-primary font-semibold text-sm tracking-widest uppercase mb-3 block">{t('insight.label')}</span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('insight.heading')}</h1>
            <p className="text-muted-foreground text-lg mb-16 max-w-2xl">{t('insight.description')}</p>
          </motion.div>

          {isError ? (
            <div className="py-16 text-center">
              <p className="text-muted-foreground mb-4">{i18n.language === 'sv' ? 'Kunde inte ladda innehållet. Försök igen om en stund.' : 'Could not load content. Please try again in a moment.'}</p>
              <button onClick={() => refetch()} disabled={isFetching} className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50">
                {isFetching ? (i18n.language === 'sv' ? 'Laddar…' : 'Loading…') : (i18n.language === 'sv' ? 'Försök igen' : 'Try again')}
              </button>
            </div>
          ) : isLoading ? (
            <div className="space-y-16">
              {[1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="h-8 w-40 bg-card rounded animate-pulse mb-6" />
                  <div className="grid md:grid-cols-3 gap-6">
                    {[1, 2, 3].map((j) => (<div key={j} className="h-48 bg-card animate-pulse" />))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-20">
              {sections?.map((section, sectionIdx) => (
                <motion.section key={section.slug} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: sectionIdx * 0.1 }}>
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold">{section.label}</h2>
                    <Link to={section.path} className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                      {t('insight.viewAll')} <ArrowRight size={16} />
                    </Link>
                  </div>
                  {section.posts.length === 0 ? (
                    <p className="text-muted-foreground">{t('insight.noPosts')}</p>
                  ) : (
                    <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
                      {section.posts.map((post: any) => (
                        <Link key={post.id} to={(post as any).__href || `/${post.slug}`} className="group flex flex-col h-full">
                          <div className="relative aspect-[4/3] overflow-hidden mb-4">
                            {post.featured_image ? (<img src={post.featured_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />) : (<div className="w-full h-full bg-card" />)}
                          </div>
                          <p className="text-xs text-muted-foreground mb-1">{formatDate(post.published_at || "")}</p>
                          <h3 className="font-display font-bold text-foreground text-lg tracking-wide mb-2 line-clamp-2">{post.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed mb-2 flex-1 line-clamp-3">{getExcerpt(post)}</p>
                          <span className="text-sm font-medium text-primary group-hover:underline inline-flex items-center gap-1 mt-auto">{t('insight.readMore')} <ArrowRight size={14} /></span>
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.section>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Blog;
